import './App.css';
// import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import HostMonitoring from './pages/HostMonitoring';
import Profile from './pages/Profile';
import Reporting from './pages/Reporting';
import UserManagement from './pages/UserManagement';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import { useAuth, hasAuthParams } from "react-oidc-context";
import React, {useState, useEffect} from 'react';
import OnboardingTour from './components/OnboardingTour';
import NavTop from './components/NavTop';
import AuthWrapper from './components/AuthWrapper';
import { UserProvider } from './context/UserContext';
import LoggedOut from './pages/LoggedOut';
import { trackPageView } from './utils/analytics';

// A simple loading component
const Loading = () => <div>Loading...</div>;

// Separate AppContent component to use useLocation hook
function AppContent({ auth, signOutRedirect }) {
  const location = useLocation(); // FIXED: Now properly using useLocation hook
  
  // Initialize showOnboarding from localStorage
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const hasCompleted = localStorage.getItem('onboardingCompleted');
    const currentStep = localStorage.getItem('onboardingCurrentStep');
    return !hasCompleted || currentStep !== null;
  });

  // Re-check onboarding status when route changes
  useEffect(() => {
    const hasCompleted = localStorage.getItem('onboardingCompleted');
    const currentStep = localStorage.getItem('onboardingCurrentStep');
    
    console.log('[App] Route changed to:', location.pathname);
    console.log('[App] onboardingCompleted:', hasCompleted);
    console.log('[App] onboardingCurrentStep:', currentStep);
    
    // Show onboarding if not completed OR if there's a saved step
    if (!hasCompleted || currentStep !== null) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [location.pathname]); // FIXED: Proper dependency on location.pathname

  const handleOnboardingComplete = () => {
    console.log('[App] Onboarding completed');
    setShowOnboarding(false);
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.removeItem('onboardingCurrentStep');
  };

  const handleOnboardingSkip = () => {
    console.log('[App] Onboarding skipped');
    setShowOnboarding(false);
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.removeItem('onboardingCurrentStep');
  };

  // Protected Route Component
  const ProtectedRoute = ({ children }) => {
    if (auth.isLoading) {
      return <Loading />;
    }

    if (auth.error) {
      return <div>Encountering error... {auth.error.message}</div>;
    }

    if (!auth.isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    return children;
  };

  return (
    <>
      {auth.isAuthenticated && (
        <div>
          <NavTop />
        </div>
      )}
      
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/logged-out" element={<LoggedOut />} />
        {/* Protected Routes */}
        <Route path="/" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
        <Route path="/reporting" element={<ProtectedRoute><Reporting /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/host-monitoring" element={<ProtectedRoute><HostMonitoring /></ProtectedRoute>} />
      </Routes>

      {/* OnboardingTour OUTSIDE Routes - persists across navigation */}
      {showOnboarding && (
        <OnboardingTour
          isActive={showOnboarding}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </>
  );
}

function App() {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // added for tracking pageviews in analytics
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);


  const signOutRedirect = () => {
    const clientId = "6025mbahv0jiru600106dqoues";
    const logoutUri = import.meta.env.VITE_LOGOUT_URI || "http://localhost:3000/logged-out";
    const cognitoDomain = "https://auth.lostlogic.io";
    
    // Set logout flag
    setIsLoggingOut(true);
    sessionStorage.setItem('isLoggingOut', 'true');
    
    // Clear all local data
    localStorage.clear();
    
    // Remove user from oidc-context
    auth.removeUser().then(() => {
      // Redirect to Cognito logout
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    });
  };

  // Silent auto-sign in logic
  React.useEffect(() => {
    // Don't auto-signin if we're in the logout process
    const loggingOut = sessionStorage.getItem('isLoggingOut');
    if (loggingOut) {
      sessionStorage.removeItem('isLoggingOut');
      setIsLoggingOut(false);
      return;
    }
    
    // Don't auto-signin if already trying or if has auth params
    if (hasAuthParams() || hasTriedSignin || isLoggingOut) {
      return;
    }
    
    // Auto-signin only if not authenticated and not loading
    if (!auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin, isLoggingOut]);

  // Loading state
  if (auth.isLoading || isLoggingOut) {
    return <div>Signing you in/out...</div>;
  }

  // Not authenticated state
  if (!auth.isAuthenticated) {
    return (
      <div className="text-center mt-5">
        <p>You are not logged in</p>
        <button 
          className="btn btn-primary" 
          onClick={() => auth.signinRedirect()}
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Wrap everything with AuthWrapper and UserProvider */}
      <AuthWrapper>
        <UserProvider>
          {/* AppContent component handles routing and onboarding */}
          <AppContent auth={auth} signOutRedirect={signOutRedirect} />
        </UserProvider>
      </AuthWrapper>
    </div>
  );
}

export default App;